import { conversationEnvironmentKeySchema } from './realtime';

/**
 * The AppSync coordinates a client needs to subscribe to the Galapagos
 * conversation plane, validated from public build-time config (ADR-094).
 *
 * The tuple is ATOMIC: one missing or malformed coordinate resolves the whole
 * thing to `null`, and a `null` tuple means the surface keeps polling rather
 * than opening a socket it cannot address. That is deliberate and it is the
 * normal case rather than an edge one — per ADR-026 Rule 9 a default
 * ticket-keyed preview synthesizes zero AppSync APIs, so most environments a
 * client is ever built for have no plane at all.
 *
 * This is the client-side enforcement point for ADR-094 Rule 7 — "any
 * missing/malformed AppSync coordinate blanks the whole realtime tuple, and a
 * malformed key resolves nothing rather than borrowing another environment's
 * values". It lives here, shared, because holding one rule at two independent
 * enforcement points is how the two clients silently drift apart: the failure
 * is asymmetric and quiet, since the rejecting client just keeps polling and
 * raises nothing.
 *
 * Two properties are worth stating because neither is obvious from the shape:
 *
 * 1. **`environmentKey` is a coordinate, not a label.** The plane filters
 *    deliveries on `environmentKey + conversationId + participantId`, so the
 *    key decides which events a client is eligible to receive. It travels as
 *    its own build binding, and CI supplies it only when the plane belongs to
 *    the environment being built — so a bundle either holds a key that matches
 *    the plane its other coordinates point at, or holds no usable tuple. It is
 *    never derived from a telemetry or estate binding: those answer different
 *    questions, and coupling them would let a change to one silently move
 *    subscription routing.
 *
 * 2. **`graphqlUrl` and `realtimeUrl` must describe the same API.** They are
 *    published separately, so a half-updated pair is reachable; accepting it
 *    would connect the socket to a different API than the one the coordinates
 *    name. The realtime host is therefore derived from the GraphQL host and the
 *    two are required to agree.
 *
 * Deliberately free of any binding-name knowledge: each app reads its own
 * prefixed environment variables and hands the raw values in, so only the
 * decision is shared, not the names.
 */
export interface ConversationRealtimeConfig {
  /**
   * Retained although no client reads it: the library derives everything it
   * needs from `graphqlUrl`. It is published as part of the tuple, it is the id
   * an operator recognises when correlating a client against a plane, and
   * validating it is part of "this tuple describes one real API" — dropping it
   * would let a build resolve a usable tuple from a partially-published one.
   */
  apiId: string;
  graphqlUrl: string;
  realtimeUrl: string;
  region: string;
  environmentKey: string;
}

/**
 * The raw coordinate values, exactly as each app read them from its own
 * bindings. Untrimmed on purpose: trimming is part of the predicate, so it
 * happens once here rather than being re-implemented at every call site.
 */
export interface ConversationRealtimeCoordinates {
  apiId?: string | undefined;
  graphqlUrl?: string | undefined;
  realtimeUrl?: string | undefined;
  region?: string | undefined;
  environmentKey?: string | undefined;
}

const APPSYNC_API_ID = /^[a-z0-9]+$/;
const AWS_REGION = /^[a-z]{2}-[a-z]+-\d+$/;

/** Escape a value before it is interpolated into a pattern. */
function escapeForPattern(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The realtime host AppSync serves for a given GraphQL endpoint, or `undefined`
 * when the endpoint is not an AppSync GraphQL URL in this region.
 *
 * Exported because it is the only place the region escaping is observable. The
 * region shape check rejects a metacharacter-carrying region before
 * `resolveConversationRealtimeConfig` ever reaches this function, so a test
 * driven through the validator returns `null` whether or not the escaping
 * exists — it cannot prove it. Asserting here can.
 */
export function expectedRealtimeUrl(graphqlUrl: string, region: string): string | undefined {
  // The region is interpolated, so a long enough one builds a pattern past the
  // engine's size limit and `new RegExp` throws — and this module promises every
  // rejection returns `null` rather than throwing. Catching the construction is
  // what keeps that promise WITHOUT narrowing what is accepted: a region that
  // compiled before still compiles and behaves identically, and only one that
  // could not have produced a usable tuple at all becomes "no plane".
  // The guard spans the MATCH as well as the construction: the engine compiles
  // the pattern lazily, so an oversized one is accepted by `new RegExp` and
  // throws later at `.match()`. A try around the constructor alone looks right
  // and catches nothing — measured.
  let host: RegExpMatchArray | null;
  try {
    const hostPattern = new RegExp(
      `^https://([a-z0-9-]+)\\.appsync-api\\.${escapeForPattern(region)}\\.amazonaws\\.com/graphql$`,
    );
    host = graphqlUrl.match(hostPattern);
  } catch {
    return undefined;
  }
  return host ? `wss://${host[1]}.appsync-realtime-api.${region}.amazonaws.com/graphql` : undefined;
}

/**
 * Assemble the tuple, or `null` if it cannot be assembled in full.
 *
 * Every rejection path returns `null` rather than throwing: a build with no
 * plane is a supported configuration, not an error, and the caller's fallback
 * is the poll it was already running.
 */
export function resolveConversationRealtimeConfig(
  coordinates: ConversationRealtimeCoordinates,
): ConversationRealtimeConfig | null {
  const apiId = coordinates.apiId?.trim();
  const graphqlUrl = coordinates.graphqlUrl?.trim();
  const realtimeUrl = coordinates.realtimeUrl?.trim();
  const region = coordinates.region?.trim();
  const environmentKey = coordinates.environmentKey?.trim();

  if (!apiId || !graphqlUrl || !realtimeUrl || !region || !environmentKey) return null;
  if (!APPSYNC_API_ID.test(apiId) || !AWS_REGION.test(region)) return null;
  // The namespace key is part of the subscription's authorization surface, so a
  // malformed value disables the tuple against the same shared shape the event
  // contract enforces server-side rather than a second copy of the rule.
  if (!conversationEnvironmentKeySchema.safeParse(environmentKey).success) return null;
  if (realtimeUrl !== expectedRealtimeUrl(graphqlUrl, region)) return null;

  return { apiId, graphqlUrl, realtimeUrl, region, environmentKey };
}
